import { useState } from 'react';
import { receptionApi, type ChangeSetView, type MenuZeroView } from '../api/client';
import { prettyNumber } from '../lib/calls';
import { useData } from '../lib/useData';
import { ReviewDrawer } from './ReviewDrawer';
import { Button, Card, LoadError, Loading, Notice } from './ui';

/**
 * Press 0 for the receptionists (1.13.0, Teampace 2714) in Settings → Reception: for each auto
 * attendant, callers who press 0 ring the receptionists (the Operators group, or the only
 * receptionist). It is the menu's own 0 key, so each change is a change set to review, read back
 * when it's applied; turning it off puts back what 0 did before.
 */
export function MenuZeroCard({ domain, writable }: { domain: string; writable: boolean }) {
  const data = useData(() => receptionApi.menuZeros(domain), [domain]);
  const [plan, setPlan] = useState<ChangeSetView | null>(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);

  if (data.error) {
    return (
      <Card title="Press 0 for the receptionists">
        <div className="card-body">
          <LoadError error={data.error} retry={data.reload} />
        </div>
      </Card>
    );
  }
  if (!data.data) {
    return (
      <Card title="Press 0 for the receptionists">
        <div className="card-body">
          <Loading label="Loading auto attendants" />
        </div>
      </Card>
    );
  }
  const { target, menus } = data.data;
  if (!menus.length) return null;

  async function change(m: MenuZeroView, on: boolean) {
    setBusy(m.extension);
    setNote(null);
    try {
      const r = await receptionApi.setMenuZero(domain, m.extension, on);
      if (r.changeSet) setPlan(r.changeSet);
      else setNote({ tone: 'ok', text: `${m.name} is already set that way.` });
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy('');
    }
  }

  const now = (m: MenuZeroView) => {
    if (m.on) return `0 rings ${target?.name ?? 'the receptionists'}`;
    if (m.blocked === 'byName') return '0 starts finding someone by name here. Choose another key for that on the auto attendant’s page first.';
    if (m.blocked === 'full') return 'All ten keys are used. Take one out on the auto attendant’s page first.';
    if (m.elsewhere) return `0 goes to ${m.elsewhereName || prettyNumber(m.elsewhere)} now. Turning this on sends it to the receptionists instead; turning it off puts this back.`;
    return 'Pressing 0 does nothing now';
  };

  return (
    <Card title="Press 0 for the receptionists">
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          {target
            ? `Callers in a menu who press 0 ring ${target.name} (${target.extension}). Say so in the menu’s greeting, for example “For anything else, press 0”.`
            : 'Make someone a receptionist first (on their page in People). With two or more, Stitch makes the Operators group, and 0 rings all of them.'}
        </p>
        <ul className="queue-backup">
          {menus.map((m) => (
            <li key={m.extension}>
              <span className="cell-stack grow">
                <strong>
                  {m.name} · {m.extension}
                </strong>
                <small className="muted">{now(m)}</small>
              </span>
              {writable && (
                <span className="queue-backup-actions">
                  <Button
                    size="small"
                    variant={m.on ? 'ghost' : 'secondary'}
                    disabled={Boolean(busy) || (!m.on && (!target || Boolean(m.blocked)))}
                    onClick={() => void change(m, !m.on)}
                    aria-label={`${m.on ? 'Turn off' : 'Turn on'} press 0 for ${m.name}`}
                  >
                    {m.on ? 'Turn off' : 'Turn on'}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
        {note && <Notice tone={note.tone}>{note.text}</Notice>}
      </div>
      {plan && (
        <ReviewDrawer
          open
          tenant={domain}
          items={[]}
          planned={plan}
          onUnstage={() => undefined}
          onClose={() => {
            setPlan(null);
            data.reload();
          }}
        />
      )}
    </Card>
  );
}
